import { ReactNode, useState } from 'react';

import { motion } from 'framer-motion';
import { toast } from 'sonner';

import { Icon } from '@/new/components/icon';
import { DocumentTabs } from '@/new/components/reference-content/document-tabs';
import { DrawerErrorState } from '@/new/components/reference-content/drawer-error-state';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

interface ChunkDrawerContentShellProps {
  isLoading: boolean;
  error: unknown;
  errorLabel: string;
  viewer: ReactNode;
  tab: { id: string; label: string };
  sourceText: string;
}

/**
 * Shared shell for the chunk-style reference drawer: a viewer above a
 * collapsible source-text panel with a copy button. Used by both case-document
 * chunks and Kim thread-file chunks.
 */
export function ChunkDrawerContentShell({
  isLoading,
  error,
  errorLabel,
  viewer,
  tab,
  sourceText,
}: ChunkDrawerContentShellProps) {
  const [isInfoPanelCollapsed, setIsInfoPanelCollapsed] = useState(false);

  if (error) return <DrawerErrorState label={errorLabel} />;
  if (isLoading) {
    return (
      <div className="my-20 flex h-full w-full items-center justify-center">
        <Icon className="lucide--loader-2 animate-spin duration-700" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="-mb-8 flex-1 overflow-hidden" onClick={() => setIsInfoPanelCollapsed(true)}>
        {viewer}
      </div>

      <div className="z-10 max-h-9/10 overflow-hidden" onMouseEnter={() => setIsInfoPanelCollapsed(false)}>
        <DocumentTabs tabs={[tab]} selectedId={tab.id} onSelect={() => {}} />

        <motion.div
          className="relative z-20 -mt-px overflow-hidden border-t bg-white text-xs shadow-[0_-1px_8px_rgba(0,0,0,0.06)]"
          animate={{ height: isInfoPanelCollapsed ? '16px' : 'auto' }}
          transition={{ duration: 0.2 }}
        >
          <div className={cn('mt-2 max-h-[calc(90vh-3rem)] overflow-y-auto px-5')}>
            <div className="my-2 flex items-center gap-1.5">
              <p className="font-medium">Source Text</p>
              <Button
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(sourceText);
                  toast.success('Source text copied to clipboard');
                }}
                className="ml-1 px-1.5 py-0.75"
              >
                <Icon className="lucide--copy" />
                <span className="text-[11px]">Copy</span>
              </Button>
            </div>

            <p className="mb-4 text-gray-700">{sourceText}</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
